import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  AlertTriangle, 
  Users, 
  Shield,
  Activity,
  GripVertical,
  Settings2,
  RotateCcw,
  Eye,
  EyeOff,
  Save,
  Monitor,
  Tablet,
  Smartphone,
  Sparkles,
  MessageSquare,
  CheckCircle,
  User,
  X,
  HelpCircle,
  Heart,
  Flame,
  CircleAlert,
  ArrowLeft,
  Mic
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Device modes configuration
const DEVICE_MODES = [
  { id: 'auto', label: 'Auto', icon: Sparkles, description: 'Automatically detect device' },
  { id: 'computer', label: 'Computer', icon: Monitor, description: 'Desktop layout' },
  { id: 'tablet', label: 'Tablet', icon: Tablet, description: 'Tablet layout' },
  { id: 'phone', label: 'Phone', icon: Smartphone, description: 'Mobile layout' },
];

// Default widget configurations - Panic Button is now right after the mode widget (before stats)
const DEFAULT_WIDGETS = [
  { id: 'panic', order: 0, visible: true, size: 'small', label: 'Emergency' },
  { id: 'comms', order: 1, visible: true, size: 'small', label: 'Non-Emergency' },
  { id: 'stats', order: 2, visible: true, size: 'full', label: 'Statistics Cards' },
  { id: 'team-status', order: 3, visible: true, size: 'medium', label: 'Team Status' },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, alerts, onlineUsers, wsConnected, sendWsMessage } = useAuth();
  const [team, setTeam] = useState([]);
  const [stats, setStats] = useState({ active: 0, resolved: 0, total: 0 });
  const [widgets, setWidgets] = useState(DEFAULT_WIDGETS);
  const [deviceMode, setDeviceMode] = useState('auto');
  const [editMode, setEditMode] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [commsMenuOpen, setCommsMenuOpen] = useState(false);
  const [commsStep, setCommsStep] = useState(1); // 1=menu, 5=select-dm-user, etc.
  const [statusUpdating, setStatusUpdating] = useState(false);
  
  // Direct message state (PTT removed)
  const [selectedDmUser, setSelectedDmUser] = useState(null);
  const [dmMessage, setDmMessage] = useState('');
  const [sendingDm, setSendingDm] = useState(false);

  useEffect(() => {
    fetchTeam();
    fetchLayout();
  }, []);

  useEffect(() => {
    const active = alerts.filter(a => a.status === 'active').length;
    const resolved = alerts.filter(a => a.status === 'resolved').length;
    setStats({ active, resolved, total: alerts.length });
  }, [alerts]);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API_URL}/team`);
      setTeam(response.data);
    } catch (error) {
      console.error('Failed to fetch team:', error);
    }
  };

  const fetchLayout = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/layout`);
      if (response.data.widgets) {
        const savedWidgets = response.data.widgets;
        const mergedWidgets = DEFAULT_WIDGETS.map(dw => {
          const saved = savedWidgets.find(sw => sw.id === dw.id);
          return saved ? { ...dw, ...saved } : dw;
        });
        setWidgets(mergedWidgets.sort((a, b) => a.order - b.order));
      }
      if (response.data.device_mode) {
        setDeviceMode(response.data.device_mode);
      }
    } catch (error) {
      console.error('Failed to fetch layout:', error);
    }
  };

  const saveLayout = async () => {
    try {
      await axios.put(`${API_URL}/dashboard/layout`, { widgets, device_mode: deviceMode });
      toast.success('Dashboard layout saved!');
      setHasChanges(false);
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to save layout');
    }
  };

  const resetLayout = async () => {
    try {
      await axios.post(`${API_URL}/dashboard/layout/reset`);
      setWidgets(DEFAULT_WIDGETS);
      setDeviceMode('auto');
      setHasChanges(false);
      toast.success('Layout reset to default');
    } catch (error) {
      toast.error('Failed to reset layout');
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(widgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    const updatedItems = items.map((item, index) => ({ ...item, order: index }));
    setWidgets(updatedItems);
    setHasChanges(true);
  };

  const toggleWidgetVisibility = (widgetId) => {
    setWidgets(widgets.map(w => 
      w.id === widgetId ? { ...w, visible: !w.visible } : w
    ));
    setHasChanges(true);
  };

  const changeDeviceMode = (mode) => {
    setDeviceMode(mode);
    setHasChanges(true);
  };

  // Determine effective device mode
  const getEffectiveMode = () => {
    if (deviceMode !== 'auto') return deviceMode;
    // Auto-detect based on window width
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) return 'phone';
      if (window.innerWidth < 1024) return 'tablet';
    }
    return 'computer';
  };

  const effectiveMode = getEffectiveMode();

  const isOnline = (userId) => onlineUsers.includes(userId);

  // Get layout classes based on device mode
  const getLayoutClasses = () => {
    switch (effectiveMode) {
      case 'phone':
        return {
          container: 'max-w-md mx-auto',
          statsGrid: 'grid-cols-2 gap-2',
          mainGrid: 'grid-cols-1 gap-4',
          cardPadding: 'p-3',
          fontSize: 'text-sm',
          panicSize: 'w-24 h-24 text-lg',
        };
      case 'tablet':
        return {
          container: 'max-w-3xl mx-auto',
          statsGrid: 'grid-cols-2 gap-3',
          mainGrid: 'grid-cols-2 gap-4',
          cardPadding: 'p-4',
          fontSize: 'text-base',
          panicSize: 'w-28 h-28 text-xl',
        };
      default: // computer
        return {
          container: 'w-full',
          statsGrid: 'grid-cols-2 md:grid-cols-4 gap-4',
          mainGrid: 'grid-cols-1 lg:grid-cols-3 gap-4',
          cardPadding: 'p-4',
          fontSize: 'text-base',
          panicSize: 'w-32 h-32 text-xl',
        };
    }
  };

  const layout = getLayoutClasses();

  // Widget components with responsive layouts
  const renderWidget = (widget) => {
    if (!widget.visible && !editMode) return null;

    const widgetContent = {
      'stats': (
        <div className={`grid ${layout.statsGrid} ${!widget.visible ? 'opacity-40' : ''}`}>
          <Card className="bg-panel border-white/10 rounded-sm card-hover">
            <CardContent className={layout.cardPadding}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Active</p>
                  <p className={`font-chivo font-black ${effectiveMode === 'phone' ? 'text-2xl' : 'text-3xl'} text-alert-red mt-1`}>{stats.active}</p>
                </div>
                <AlertTriangle className={`${effectiveMode === 'phone' ? 'w-6 h-6' : 'w-8 h-8'} text-alert-red opacity-50`} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-panel border-white/10 rounded-sm card-hover">
            <CardContent className={layout.cardPadding}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Online</p>
                  <p className={`font-chivo font-black ${effectiveMode === 'phone' ? 'text-2xl' : 'text-3xl'} text-green-500 mt-1`}>{onlineUsers.length}</p>
                </div>
                <Users className={`${effectiveMode === 'phone' ? 'w-6 h-6' : 'w-8 h-8'} text-green-500 opacity-50`} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-panel border-white/10 rounded-sm card-hover">
            <CardContent className={layout.cardPadding}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Resolved</p>
                  <p className={`font-chivo font-black ${effectiveMode === 'phone' ? 'text-2xl' : 'text-3xl'} text-primary mt-1`}>{stats.resolved}</p>
                </div>
                <Activity className={`${effectiveMode === 'phone' ? 'w-6 h-6' : 'w-8 h-8'} text-primary opacity-50`} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-panel border-white/10 rounded-sm card-hover">
            <CardContent className={layout.cardPadding}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Team</p>
                  <p className={`font-chivo font-black ${effectiveMode === 'phone' ? 'text-2xl' : 'text-3xl'} text-white mt-1`}>{team.length}</p>
                </div>
                <Shield className={`${effectiveMode === 'phone' ? 'w-6 h-6' : 'w-8 h-8'} text-white opacity-50`} />
              </div>
            </CardContent>
          </Card>
        </div>
      ),

      'panic': (
        <Card className={`bg-panel border-white/10 rounded-sm ${!widget.visible ? 'opacity-40' : ''}`}>
          <CardHeader className="border-b border-white/10 pb-3">
            <CardTitle className={`font-chivo font-bold ${effectiveMode === 'phone' ? 'text-base' : 'text-lg'} uppercase tracking-tight text-white`}>
              Emergency
            </CardTitle>
          </CardHeader>
          <CardContent className={`${effectiveMode === 'phone' ? 'p-4' : 'p-6'} flex justify-center`}>
            <Link to="/panic">
              <button className={`${layout.panicSize} rounded-full bg-alert-red hover:bg-red-700 text-white font-chivo font-black uppercase tracking-wide panic-button transition-colors`}>
                PANIC
              </button>
            </Link>
          </CardContent>
        </Card>
      ),

      'comms': (
        <Card className={`bg-panel border-white/10 rounded-sm ${!widget.visible ? 'opacity-40' : ''}`}>
          <CardHeader className="border-b border-white/10 pb-3">
            <CardTitle className={`font-chivo font-bold ${effectiveMode === 'phone' ? 'text-base' : 'text-lg'} uppercase tracking-tight text-white`}>
              Non-Emergency
            </CardTitle>
          </CardHeader>
          <CardContent className={`${effectiveMode === 'phone' ? 'p-4' : 'p-6'}`}>
            {commsStep === 1 ? (
              /* Step 1: Main Menu */
              <div className="space-y-3">
                {/* Potential Issue Button - Full Width at Top */}
                <button
                  onClick={() => setCommsStep(7)}
                  className="w-full p-4 bg-surface hover:bg-orange-900/30 rounded-sm border-2 border-orange-500/30 hover:border-orange-500 transition-all text-center"
                  data-testid="comms-potential-issue"
                >
                  <CircleAlert className="w-8 h-8 mx-auto mb-2 text-orange-500" />
                  <span className="font-mono text-sm font-bold text-orange-500">Potential Issue</span>
                  <p className="font-mono text-[10px] text-muted-foreground mt-1">Report a non-emergency concern</p>
                </button>

                {/* Chat Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => navigate('/chat')}
                    className="p-4 bg-surface hover:bg-purple-900/30 rounded-sm border-2 border-purple-500/30 hover:border-purple-500 transition-all text-center"
                  >
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 text-purple-500" />
                    <span className="font-mono text-sm font-bold text-purple-500">Group Chat</span>
                    <p className="font-mono text-[10px] text-muted-foreground mt-1">Message all team</p>
                  </button>
                  <button
                    onClick={() => setCommsStep(5)}
                    className="p-4 bg-surface hover:bg-cyan-900/30 rounded-sm border-2 border-cyan-500/30 hover:border-cyan-500 transition-all text-center"
                  >
                    <User className="w-8 h-8 mx-auto mb-2 text-cyan-500" />
                    <span className="font-mono text-sm font-bold text-cyan-500">Direct Message</span>
                    <p className="font-mono text-[10px] text-muted-foreground mt-1">Message one person</p>
                  </button>
                </div>
              </div>
            ) : commsStep === 5 ? (
              /* Step 5: Select User for Direct Message */
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <button 
                    onClick={() => setCommsStep(1)}
                    className="flex items-center gap-1 text-muted-foreground hover:text-white"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="font-mono text-xs">Back</span>
                  </button>
                  <span className="font-mono text-xs text-cyan-500 uppercase font-bold">Select Recipient</span>
                </div>
                
                <ScrollArea className="h-48">
                  <div className="space-y-1">
                    {team.filter(m => m.id !== user?.id && m.status === 'active').map((member) => {
                      const isOnline = onlineUsers?.includes(member.id);
                      return (
                        <button
                          key={member.id}
                          onClick={() => { setSelectedDmUser(member); setCommsStep(6); }}
                          className="w-full flex items-center gap-3 p-2 bg-surface hover:bg-cyan-900/30 rounded-sm border border-white/10 hover:border-cyan-500/50 transition-all"
                        >
                          <div className="relative">
                            <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                              <User className="w-4 h-4 text-cyan-500" />
                            </div>
                            <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-panel ${isOnline ? 'bg-green-500' : 'bg-gray-500'}`} />
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-inter text-sm text-white">{member.name}</p>
                            <p className="font-mono text-[10px] text-muted-foreground">{member.role?.replace(/_/g, ' ')}</p>
                          </div>
                          <MessageSquare className="w-4 h-4 text-cyan-500" />
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            ) : commsStep === 6 ? (
              /* Step 6: Compose Direct Message */
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <button 
                    onClick={() => { setCommsStep(5); setSelectedDmUser(null); setDmMessage(''); }}
                    className="flex items-center gap-1 text-muted-foreground hover:text-white"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="font-mono text-xs">Back</span>
                  </button>
                  <span className="font-mono text-xs text-cyan-500 uppercase font-bold">
                    Message {selectedDmUser?.name}
                  </span>
                </div>
                
                {/* Recipient Info */}
                <div className="flex items-center gap-3 p-3 bg-surface rounded-sm border border-cyan-500/30">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-cyan-500" />
                  </div>
                  <div>
                    <p className="font-inter text-sm text-white font-bold">{selectedDmUser?.name}</p>
                    <p className="font-mono text-[10px] text-muted-foreground">{selectedDmUser?.role?.replace(/_/g, ' ')}</p>
                  </div>
                </div>
                
                {/* Message Input */}
                <div className="space-y-2">
                  <textarea
                    value={dmMessage}
                    onChange={(e) => setDmMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="w-full h-24 px-3 py-2 bg-surface border border-white/10 rounded-sm text-white text-sm font-inter placeholder:text-muted-foreground focus:border-cyan-500/50 focus:outline-none resize-none"
                    autoFocus
                  />
                  <Button
                    onClick={sendDirectMessage}
                    disabled={!dmMessage.trim() || sendingDm}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-mono uppercase rounded-sm"
                  >
                    {sendingDm ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </div>
            ) : commsStep === 7 ? (
              /* Step 7: Potential Issue - Report Options */
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <button 
                    onClick={() => setCommsStep(1)}
                    className="flex items-center gap-1 text-muted-foreground hover:text-white"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="font-mono text-xs">Back</span>
                  </button>
                  <span className="font-mono text-xs text-orange-500 uppercase font-bold">Potential Issue</span>
                </div>
                
                <div className="text-center py-2">
                  <CircleAlert className="w-10 h-10 mx-auto mb-2 text-orange-500" />
                  <p className="font-chivo font-bold text-orange-500 uppercase">Report a Concern</p>
                  <p className="font-mono text-[10px] text-muted-foreground mt-1">Non-emergency, but needs attention</p>
                </div>

                <div className="space-y-2">
                  <button 
                    onClick={() => navigate('/chat?tab=group')}
                    className="w-full flex items-center gap-3 p-4 bg-surface hover:bg-purple-900/30 rounded-sm border-2 border-purple-500/30 hover:border-purple-500 transition-all"
                  >
                    <MessageSquare className="w-6 h-6 text-purple-500" />
                    <div className="text-left">
                      <span className="font-mono text-sm font-bold text-purple-500">Report via Chat</span>
                      <p className="font-mono text-[10px] text-muted-foreground">Message to team</p>
                    </div>
                  </button>
                  <button 
                    onClick={() => setCommsStep(5)}
                    className="w-full flex items-center gap-3 p-4 bg-surface hover:bg-cyan-900/30 rounded-sm border-2 border-cyan-500/30 hover:border-cyan-500 transition-all"
                  >
                    <User className="w-6 h-6 text-cyan-500" />
                    <div className="text-left">
                      <span className="font-mono text-sm font-bold text-cyan-500">Report to Individual</span>
                      <p className="font-mono text-[10px] text-muted-foreground">Direct message</p>
                    </div>
                  </button>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ),

      'team-status': (
        <Card className={`bg-panel border-white/10 rounded-sm ${!widget.visible ? 'opacity-40' : ''}`}>
          <CardHeader className="border-b border-white/10 pb-3">
            <CardTitle className={`font-chivo font-bold ${effectiveMode === 'phone' ? 'text-base' : 'text-lg'} uppercase tracking-tight text-white flex items-center gap-2`}>
              <Users className="w-4 h-4 text-green-500" />
              Team
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3">
            <ScrollArea className={effectiveMode === 'phone' ? 'h-[150px]' : 'h-[180px]'}>
              <div className="space-y-2">
                {[...team]
                  .sort((a, b) => {
                    // Online members first, offline second
                    const aOnline = isOnline(a.id) ? 0 : 1;
                    const bOnline = isOnline(b.id) ? 0 : 1;
                    return aOnline - bOnline;
                  })
                  .slice(0, 8)
                  .map((member) => (
                  <div key={member.id} className="flex items-center gap-2 p-1.5 rounded-sm hover:bg-white/5">
                    <div className="relative">
                      <div className="w-7 h-7 rounded-sm bg-surface flex items-center justify-center">
                        <span className="font-chivo font-bold text-[10px] text-white">
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      {isOnline(member.id) && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border border-panel" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-inter text-xs text-white truncate">{member.name}</p>
                      <p className="font-mono text-[9px] text-muted-foreground uppercase">{member.role.replace('_', ' ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      ),
    };

    return widgetContent[widget.id] || null;
  };

  const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);
  const visibleWidgets = editMode ? sortedWidgets : sortedWidgets.filter(w => w.visible);

  return (
    <div className={`p-4 md:p-6 space-y-4 ${layout.container}`} data-testid="dashboard">
      {/* Header with Logo */}
      <div className="flex items-center justify-between mb-4">
        <PageHeader 
          title="Control Center" 
          subtitle={`Welcome, ${user?.name?.split(' ')[0]}`}
        />
        
        {/* Settings Icon */}
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="icon" className="border-white/20 rounded-sm h-10 w-10">
              <Settings2 className="w-5 h-5" />
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-panel border-white/10 rounded-sm max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-chivo font-bold text-lg text-white">Dashboard Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {/* Device Mode Selection */}
              <div>
                <p className="font-mono text-xs uppercase text-muted-foreground mb-2">View Mode</p>
                <div className="grid grid-cols-2 gap-2">
                  {DEVICE_MODES.map((mode) => {
                    const Icon = mode.icon;
                    const isActive = deviceMode === mode.id;
                    return (
                      <button
                        key={mode.id}
                        onClick={() => changeDeviceMode(mode.id)}
                        className={`flex items-center gap-2 p-3 rounded-sm border transition-colors ${
                          isActive 
                            ? 'bg-primary/20 border-primary text-white' 
                            : 'border-white/10 text-muted-foreground hover:border-white/20'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <div className="text-left">
                          <p className="font-inter text-sm">{mode.label}</p>
                          <p className="font-mono text-[9px] opacity-70">{mode.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Widget Visibility */}
              <div>
                <p className="font-mono text-xs uppercase text-muted-foreground mb-2">Widgets</p>
                <div className="space-y-2">
                  {sortedWidgets
                    .filter((widget) => widget.id !== 'panic' && widget.id !== 'comms')
                    .map((widget) => (
                    <div key={widget.id} className="flex items-center justify-between p-2 bg-surface rounded-sm">
                      <span className="font-inter text-sm text-white">{widget.label}</span>
                      <Switch checked={widget.visible} onCheckedChange={() => toggleWidgetVisibility(widget.id)} />
                    </div>
                  ))}
                </div>
              </div>

              <Button variant="outline" onClick={resetLayout} className="w-full border-white/20 rounded-sm font-mono text-xs">
                <RotateCcw className="w-3 h-3 mr-2" />
                Reset to Default
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Widgets */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="dashboard-widgets">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
              {visibleWidgets.map((widget, index) => (
                <Draggable key={widget.id} draggableId={widget.id} index={index} isDragDisabled={true}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={snapshot.isDragging ? 'opacity-70' : ''}
                    >
                      <div className="relative">
                        <div>
                          {renderWidget(widget)}
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default Dashboard;
